/**
 * Notification API Service
 *
 * Calls to the cross-service Notification API (bell icon / notification inbox).
 * See https://notification.staging-api.boostt.ai/docs
 */

import { apiConfig } from '@/config/apiConfig';

export type NotificationResponse = {
  id: string;
  user_id: string;
  account_id: string | null;
  source_service: string;
  event_type: string;
  title: string;
  message: string;
  deep_link: string | null;
  metadata: Record<string, unknown> | null;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
};

export type NotificationListResponse = {
  notifications: NotificationResponse[];
  unread_count: number;
};

function authHeaders(token?: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listNotifications(
  token: string | null | undefined,
  params: { unreadOnly?: boolean; limit?: number; offset?: number } = {}
): Promise<NotificationListResponse> {
  const query = new URLSearchParams();
  if (params.unreadOnly) query.set('unread_only', 'true');
  if (params.limit != null) query.set('limit', String(params.limit));
  if (params.offset != null) query.set('offset', String(params.offset));

  const res = await fetch(
    `${apiConfig.baseUrls.notification}${apiConfig.notificationEndpoints.list}?${query}`,
    { credentials: 'include', headers: authHeaders(token) }
  );
  if (!res.ok) {
    throw new Error(`Failed to load notifications (${res.status})`);
  }
  return res.json();
}

export async function markNotificationRead(
  token: string | null | undefined,
  notificationId: string
): Promise<NotificationResponse> {
  const res = await fetch(
    `${apiConfig.baseUrls.notification}${apiConfig.notificationEndpoints.markRead(notificationId)}`,
    { method: 'POST', credentials: 'include', headers: authHeaders(token) }
  );
  if (!res.ok) {
    throw new Error(`Failed to mark notification as read (${res.status})`);
  }
  return res.json();
}
